import { A } from "@solidjs/router";
import Text from "../components/Text";
import { Show, onMount, onCleanup } from "solid-js";
import { useAdminAccess } from "./useAdminAccess";
import { useTranslation } from "../hooks/useTranslation";
import { useMobileSidebar } from "../hooks/useMobileSidebar";
import { useSidebarCollapse } from "../hooks/useSidebarCollapse";
import FeatureGate from "../components/FeatureGate";

interface AdminNavbarProps {
    children: any;
}

export function AdminNavbar({ children }: AdminNavbarProps) {

    const { isRoot } = useAdminAccess();
    const { t } = useTranslation();
    const { sidebarOpen, closeSidebar } = useMobileSidebar();
    const { collapsed, toggleCollapse } = useSidebarCollapse();

    const nav = () => {
        if (window.innerWidth < 768) closeSidebar();
    };

    let sidebarRef: HTMLDivElement | undefined;
    const SIDEBAR_SCROLL_KEY = 'admin-sidebar-scroll';
    const saveSidebarScroll = () => {
        if (sidebarRef) sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(sidebarRef.scrollTop));
    };
    onMount(() => {
        if (!sidebarRef) return;
        const saved = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
        if (saved) sidebarRef.scrollTop = parseInt(saved, 10) || 0;
        sidebarRef.addEventListener('scroll', saveSidebarScroll, { passive: true });
        onCleanup(() => sidebarRef?.removeEventListener('scroll', saveSidebarScroll));
    });

    return (
        <div class="dashboard-container">
            <Show when={sidebarOpen()}>
                <div class="sidebar-backdrop" onClick={closeSidebar} />
            </Show>
            <div ref={sidebarRef} class={"dashboard-sidebar admin-sidebar" + (sidebarOpen() ? " sidebar-open" : "") + (collapsed() ? " sidebar-collapsed" : "")}>

                <span class="sidebar-section-label">{t('SIDEBAR_ADMIN')}</span>
                <A activeClass="active" inactiveClass="inactive" href="/admin" end onClick={nav}>
                    <i class="fa-solid fa-user-tie"></i><Text val="ADMIN" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/admin/tenants" onClick={nav}>
                    <i class="fa-solid fa-building"></i><Text val="ADMIN_TENANT_MANAGEMENT" />
                </A>
                <FeatureGate flag="VOTING">
                    <A activeClass="active" inactiveClass="inactive" href="/admin/voting" onClick={nav}>
                        <i class="fa-solid fa-square-poll-vertical"></i><Text val="ADMIN_VOTING_NAV" />
                    </A>
                    <Show when={isRoot()}>
                        <A activeClass="active" inactiveClass="inactive" href="/admin/voting/proxies" onClick={nav}>
                            <i class="fa-solid fa-network-wired"></i><Text val="ADMIN_VOTING_PROXIES_NAV" />
                        </A>
                    </Show>
                </FeatureGate>
                <A activeClass="active" inactiveClass="inactive" href="/admin/alerts" onClick={nav}>
                    <i class="fa-solid fa-bell"></i><Text val="ADMIN_ALERTS_NAV" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/admin/reconnect-windows" onClick={nav}>
                    <i class="fa-solid fa-clock"></i><Text val="ADMIN_RECONNECT_WINDOWS_NAV" />
                </A>
                <FeatureGate flag="SELF_SHUTOFF">
                    <A activeClass="active" inactiveClass="inactive" href="/admin/blacklist" onClick={nav}>
                        <i class="fa-solid fa-user-slash"></i><Text val="ADMIN_BLACKLIST_NAV" />
                    </A>
                </FeatureGate>
                <A activeClass="active" inactiveClass="inactive" href="/admin/botshop-domains" onClick={nav}>
                    <i class="fa-solid fa-globe"></i><Text val="ADMIN_BOTSHOP_DOMAINS_NAV" />
                </A>
                <FeatureGate flag="CITYBUILD_STATUS">
                    <A activeClass="active" inactiveClass="inactive" href="/admin/citybuild-status" onClick={nav}>
                        <i class="fa-solid fa-tower-broadcast"></i><Text val="CITYBUILD_STATUS_NAV" />
                    </A>
                </FeatureGate>
                <A activeClass="active" inactiveClass="inactive" href="/admin/goggles-monitoring" onClick={nav}>
                    <i class="fa-solid fa-wave-square"></i><Text val="VEKTOR_MONITOR_TITLE" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/admin/packet-recordings" onClick={nav}>
                    <i class="fa-solid fa-file-waveform"></i><Text val="PACKET_RECORDING_TITLE" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/admin/task-capsules" onClick={nav}>
                    <i class="fa-solid fa-box-archive"></i><Text val="TASK_CAPSULE_TITLE" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/admin/custom-items" onClick={nav}>
                    <i class="fa-solid fa-tags"></i><Text val="CUSTOM_ITEMS_NAV" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/admin/koffermatic-performance" onClick={nav}>
                    <i class="fa-solid fa-gauge-high"></i><Text val="KOFFERMATIC_PERF_TITLE" />
                </A>
                <FeatureGate flag="CASE_OPENING">
                    <A activeClass="active" inactiveClass="inactive" href="/admin/case-stats" onClick={nav}>
                        <i class="fa-solid fa-box-open"></i><Text val="ADMIN_CASE_STATS_NAV" />
                    </A>
                </FeatureGate>
                <FeatureGate flag="BOTENGINE_STATS">
                    <A activeClass="active" inactiveClass="inactive" href="/botengine/stats" onClick={nav}>
                        <i class="fa-solid fa-chart-bar"></i><Text val="ENGINE_STATS_NAV" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/botengine/history" onClick={nav}>
                        <i class="fa-solid fa-clock-rotate-left"></i><Text val="ENGINE_HISTORY_NAV" />
                    </A>
                </FeatureGate>
                <Show when={isRoot()}>
                    <A activeClass="active" inactiveClass="inactive" href="/admin/bots" onClick={nav}>
                        <i class="fa-solid fa-id-card-clip"></i><Text val="BOT_REGISTRY_ADMIN_NAV" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/admin/proxies" onClick={nav}>
                        <i class="fa-solid fa-network-wired"></i><Text val="PROXY_NAV" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/admin/licenses" onClick={nav}>
                        <i class="fa-solid fa-id-badge"></i><Text val="ADMIN_LICENSES_NAV" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/admin/schematic-licenses" onClick={nav}>
                        <i class="fa-solid fa-key"></i><Text val="ADMIN_SCHEMATIC_LICENSES_NAV" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/admin/feature-flags" onClick={nav}>
                        <i class="fa-solid fa-flag"></i><Text val="FEATURE_FLAGS_NAV" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/admin/compressed-placement" onClick={nav}>
                        <i class="fa-solid fa-cubes-stacked"></i><Text val="COMPRESSED_PLACEMENT_NAV" />
                    </A>
                    <A activeClass="active" inactiveClass="inactive" href="/admin/possessions" onClick={nav}>
                        <i class="fa-solid fa-ghost"></i><Text val="VEKTOR_POSSESSIONS_TITLE" />
                    </A>
                </Show>
                <A activeClass="active" inactiveClass="inactive" href="/log" onClick={nav}>
                    <i class="fa-solid fa-font-awesome"></i><Text val="LOG" />
                </A>
                <A activeClass="active" inactiveClass="inactive" href="/eventviewer" onClick={nav}>
                    <i class="fa-solid fa-bolt-lightning"></i><Text val="EVENTVIEWER" />
                </A>

                <span class="sidebar-section-label">{t('SIDEBAR_SETTINGS')}</span>
                <A activeClass="active" inactiveClass="inactive" href="/admin/settings" onClick={nav}>
                    <i class="fa-solid fa-sliders"></i><Text val="ADMIN_SETTINGS_NAV" />
                </A>

                <button
                    class="sidebar-collapse-toggle"
                    onClick={toggleCollapse}
                    title={collapsed() ? t('SIDEBAR_EXPAND') : t('SIDEBAR_COLLAPSE')}
                    aria-label={collapsed() ? t('SIDEBAR_EXPAND') : t('SIDEBAR_COLLAPSE')}
                >
                    <i class={`fa-solid ${collapsed() ? "fa-angles-right" : "fa-angles-left"}`} />
                    <span class="sidebar-collapse-label"><Text val="SIDEBAR_COLLAPSE_LABEL" /></span>
                </button>

            </div>
            <div class="dashboard-content">
                {children}
            </div>
        </div>
    );

}
